/**
 * Everything this ticket is connected to — ONE list.
 *
 * Kai, three times: *"the associations mean the same thing"*, then *"on
 * both?!?!?!"* looking at two tabs that each listed connected tickets, then
 * *"what's the difference between them? these feel like they can just be
 * one."*
 *
 * They were right each time. Underneath there are three tables and they are
 * genuinely different — a hierarchy (`parent_id`), a typed ITIL relation
 * (`ticket_associations`, with `caused` vs `resulted`), and Step 21's
 * symmetric untyped link. But that is a STORAGE distinction, and mirroring it
 * in the UI produced three lists answering one question: *what else is
 * involved?*
 *
 * So: one list, one Add, and the relationship stated on the row. The
 * distinction is still there — it is just carried where a person can read it
 * rather than by which box they had to look in.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Modal } from '../../components/ui/modal';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { ticketRef } from '@itsm/shared';
import { TicketPicker } from './TicketPicker';
import type { TicketRow } from './api';
import { ticketPath } from './api';

interface Assoc {
  rowId: string;
  relation: string;
  id: string;
  number: number;
  type: string;
  subject: string;
}
interface Child {
  id: string;
  number: number;
  type: string;
  subject: string;
}
interface Linked {
  id: string;
  number: number;
  type: string;
  subject: string;
}

/** Every relationship, from THIS ticket's point of view. The label is what
 *  the row says; the rest is how it is written and unwritten. */
type Kind = 'parent' | 'child' | 'prompted' | 'promptedBy' | 'resultedFrom' | 'ledTo' | 'linked';

const LABEL: Record<Kind, string> = {
  parent: 'Parent',
  child: 'Child',
  prompted: 'This prompted',
  promptedBy: 'Prompted this',
  resultedFrom: 'This resulted from',
  ledTo: 'Resulted from this',
  linked: 'Related',
};

interface Row {
  key: string;
  kind: Kind;
  id: string;
  number: number;
  type: string;
  subject: string;
  /** How to undo it — the three tables need three different calls. */
  remove: () => Promise<unknown>;
}

export function ConnectionsTab({
  id,
  type,
  parent,
  links,
  canEdit,
}: {
  id: string;
  type: string;
  parent: { id: string; number: number; type: string; subject: string } | null;
  links: Linked[];
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [picked, setPicked] = useState<TicketRow | null>(null);
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<Kind>('linked');

  const children = useQuery({
    queryKey: qk.tickets.children(id),
    queryFn: () => apiFetch<{ entries: Child[] }>(`/api/tickets/${id}/children`),
  });
  const assoc = useQuery({
    queryKey: qk.tickets.associations(id),
    queryFn: () =>
      apiFetch<{ associatedTo: Assoc[]; associated: Assoc[] }>(`/api/tickets/${id}/associations`),
  });

  const refresh = () => {
    for (const k of [
      qk.tickets.children(id),
      qk.tickets.associations(id),
      qk.tickets.detail(id),
      qk.tickets.activity(id),
    ])
      void qc.invalidateQueries({ queryKey: k });
  };
  const send = (p: Promise<{ ok: boolean; error?: string | null }>) =>
    p.then((r) => {
      if (!r.ok) toast.error(r.error ?? 'Could not do that.');
      refresh();
      return r;
    });

  const rows: Row[] = useMemo(() => {
    const out: Row[] = [];
    if (parent)
      out.push({
        key: `p:${parent.id}`,
        kind: 'parent',
        ...parent,
        remove: () => send(apiSend(`/api/tickets/${id}`, 'PATCH', { parentId: null })),
      });
    for (const c of children.data?.entries ?? [])
      out.push({
        key: `c:${c.id}`,
        kind: 'child',
        ...c,
        remove: () => send(apiSend(`/api/tickets/${c.id}`, 'PATCH', { parentId: null })),
      });
    for (const a of assoc.data?.associatedTo ?? [])
      out.push({
        key: `u:${a.rowId}`,
        kind: a.relation === 'resulted' ? 'resultedFrom' : 'prompted',
        ...a,
        remove: () => send(apiSend(`/api/tickets/${id}/associations/${a.rowId}`, 'DELETE')),
      });
    for (const a of assoc.data?.associated ?? [])
      out.push({
        key: `d:${a.rowId}`,
        kind: a.relation === 'resulted' ? 'ledTo' : 'promptedBy',
        ...a,
        // The row lives on the OTHER ticket, so it is deleted from there.
        remove: () => send(apiSend(`/api/tickets/${a.id}/associations/${a.rowId}`, 'DELETE')),
      });
    for (const l of links)
      out.push({
        key: `l:${l.id}`,
        kind: 'linked',
        ...l,
        remove: () => send(apiSend(`/api/tickets/${id}/links/${l.id}`, 'DELETE')),
      });
    return out;
  }, [parent, children.data, assoc.data, links, id]);

  /**
   * Which relationships make sense for the pair. Offering all seven and
   * letting the API refuse four of them would be a picker that mostly does
   * not work.
   */
  const optionsFor = (other: TicketRow): Kind[] => {
    const umbrella = (t: string) => t === 'problem' || t === 'change';
    const ks: Kind[] = ['linked', 'parent', 'child'];
    if (umbrella(other.type)) ks.push('prompted', 'resultedFrom');
    if (umbrella(type)) ks.push('promptedBy', 'ledTo');
    return ks;
  };

  const commit = async () => {
    if (!picked) return;
    const other = picked.id;
    setAdding(true);
    try {
      if (kind === 'linked') await send(apiSend(`/api/tickets/${id}/links`, 'POST', { linkedId: other }));
      else if (kind === 'parent') await send(apiSend(`/api/tickets/${id}`, 'PATCH', { parentId: other }));
      else if (kind === 'child') await send(apiSend(`/api/tickets/${other}`, 'PATCH', { parentId: id }));
      else if (kind === 'prompted')
        await send(
          apiSend(`/api/tickets/${id}/associations`, 'POST', { associatedToId: other, relation: 'caused' }),
        );
      else if (kind === 'resultedFrom')
        await send(
          apiSend(`/api/tickets/${id}/associations`, 'POST', { associatedToId: other, relation: 'resulted' }),
        );
      else if (kind === 'promptedBy')
        await send(
          apiSend(`/api/tickets/${other}/associations`, 'POST', { associatedToId: id, relation: 'caused' }),
        );
      else if (kind === 'ledTo')
        await send(
          apiSend(`/api/tickets/${other}/associations`, 'POST', { associatedToId: id, relation: 'resulted' }),
        );
      setPicked(null);
      setOpen(false);
    } finally {
      setAdding(false);
    }
  };

  const loading = children.isPending || assoc.isPending;

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">Connected tickets</h3>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            onClick={() => {
              setKind('linked');
              setPicked(null);
              setOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Connect a ticket
          </Button>
        )}
      </div>
      {loading && <Skeleton className="h-10 w-full" />}
      {!loading && rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing connected.</p>}

      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-2 rounded-md border px-3 py-2 hover:bg-accent/40">
            {/* The relationship, on the row. This is the whole point of one
                list: the distinction is readable instead of implied by which
                box it sat in. */}
            <span className="w-36 shrink-0 text-xs font-medium text-muted-foreground">{LABEL[r.kind]}</span>
            <button
              type="button"
              onClick={() => navigate(ticketPath(r.type, r.id))}
              className="min-w-0 flex-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="mr-2 text-xs tabular-nums text-muted-foreground">
                {ticketRef(r.type, r.number)}
              </span>
              <span className="text-sm">{r.subject}</span>
            </button>
            {canEdit && (
              <Button size="sm" variant="ghost" onClick={() => void r.remove()} aria-label="Disconnect">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Two steps in one modal: which ticket, then how it relates. Asking for
          the relationship FIRST would mean choosing from a list half of which
          is invalid until the other ticket is known. */}
      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setPicked(null);
        }}
        title="Connect a ticket"
      >
        <div className="space-y-3 p-4">
          {!picked ? (
            <TicketPicker excludeId={id} onPick={setPicked} />
          ) : (
            <>
              <div className="rounded-md border px-3 py-2 text-sm">
                <span className="mr-2 text-xs tabular-nums text-muted-foreground">
                  {ticketRef(picked.type, picked.number)}
                </span>
                {picked.subject}
                <Button size="sm" variant="ghost" className="ml-2" onClick={() => setPicked(null)}>
                  Change
                </Button>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  How does it relate?
                </label>
                <Select value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                  {optionsFor(picked).map((k) => (
                    <option key={k} value={k}>
                      {LABEL[k]}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button disabled={adding} onClick={() => void commit()}>
                  {adding ? 'Connecting…' : 'Connect'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
